/**
 * Learning Center Types
 * Tutorial system with completion badges and user progress tracking
 */
export var TutorialCategory;
(function (TutorialCategory) {
    TutorialCategory["GETTING_STARTED"] = "getting-started";
    TutorialCategory["DIAMOND_SEARCH"] = "diamond-search";
    TutorialCategory["CERTIFICATION"] = "certification";
    TutorialCategory["MEMO_SYSTEM"] = "memo-system";
    TutorialCategory["ADVANCED_FEATURES"] = "advanced-features";
    TutorialCategory["PLATFORM_TOOLS"] = "platform-tools";
    TutorialCategory["ADMIN_ONLY"] = "admin-only";
})(TutorialCategory || (TutorialCategory = {}));
export var BadgeTier;
(function (BadgeTier) {
    BadgeTier["BRONZE"] = "bronze";
    BadgeTier["SILVER"] = "silver";
    BadgeTier["GOLD"] = "gold";
    BadgeTier["PLATINUM"] = "platinum";
})(BadgeTier || (BadgeTier = {}));
/**
 * Chapter Update Type
 * Determines if user needs to review updated content
 */
export var ChapterUpdateType;
(function (ChapterUpdateType) {
    ChapterUpdateType["NONE"] = "none";
    ChapterUpdateType["MINOR"] = "minor";
    ChapterUpdateType["MAJOR"] = "major"; // Required update (new features, significant changes)
})(ChapterUpdateType || (ChapterUpdateType = {}));
/**
 * Tutorial Category Configuration
 */
export const TUTORIAL_CATEGORY_CONFIG = {
    [TutorialCategory.GETTING_STARTED]: {
        label: 'Getting Started',
        description: 'Learn the basics of the Royal Gems Platform',
        color: '#22c55e',
        icon: 'FaRocket'
    },
    [TutorialCategory.DIAMOND_SEARCH]: {
        label: 'Diamond Search',
        description: 'Master advanced search and filtering',
        color: '#3b82f6',
        icon: 'FaSearch'
    },
    [TutorialCategory.CERTIFICATION]: {
        label: 'Certification',
        description: 'Understanding diamond certifications',
        color: '#8b5cf6',
        icon: 'FaCertificate'
    },
    [TutorialCategory.MEMO_SYSTEM]: {
        label: 'Memo System',
        description: 'Request and manage diamond memos',
        color: '#f59e0b',
        icon: 'FaClipboardList'
    },
    [TutorialCategory.ADVANCED_FEATURES]: {
        label: 'Advanced Features',
        description: 'Unlock powerful platform capabilities',
        color: '#ec4899',
        icon: 'FaStar'
    },
    [TutorialCategory.PLATFORM_TOOLS]: {
        label: 'Platform Tools',
        description: 'Export, compare, and manage inventory',
        color: '#06b6d4',
        icon: 'FaTools'
    },
    [TutorialCategory.ADMIN_ONLY]: {
        label: 'Admin Training',
        description: 'Administrative tools and system management',
        color: '#dc2626',
        icon: 'FaUserShield'
    }
};
/**
 * Badge Tier Configuration
 */
export const BADGE_TIER_CONFIG = {
    [BadgeTier.BRONZE]: {
        label: 'Bronze',
        color: '#cd7f32',
        gradient: 'linear-gradient(135deg, #cd7f32 0%, #e6a857 100%)'
    },
    [BadgeTier.SILVER]: {
        label: 'Silver',
        color: '#c0c0c0',
        gradient: 'linear-gradient(135deg, #c0c0c0 0%, #e8e8e8 100%)'
    },
    [BadgeTier.GOLD]: {
        label: 'Gold',
        color: '#ffd700',
        gradient: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)'
    },
    [BadgeTier.PLATINUM]: {
        label: 'Platinum',
        color: '#e5e4e2',
        gradient: 'linear-gradient(135deg, #b0c4de 0%, #e5e4e2 100%)'
    }
};
/**
 * Helper Functions for Version Comparison
 */
/**
 * Compare two semantic versions
 * Returns: 1 if v1 > v2, -1 if v1 < v2, 0 if equal
 */
export const compareVersions = (v1, v2) => {
    const parts1 = v1.split('.').map(Number);
    const parts2 = v2.split('.').map(Number);
    for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
        const num1 = parts1[i] || 0;
        const num2 = parts2[i] || 0;
        if (num1 > num2)
            return 1;
        if (num1 < num2)
            return -1;
    }
    return 0;
};
/**
 * Determine if an update is available and its type
 */
export const getUpdateType = (completedVersion, currentVersion, changeLog) => {
    if (!completedVersion || completedVersion === currentVersion) {
        return ChapterUpdateType.NONE;
    }
    // If changelog exists, get the most severe update type between versions
    if (changeLog && changeLog.length > 0) {
        const relevantChanges = changeLog.filter(entry => compareVersions(entry.version, completedVersion) > 0 &&
            compareVersions(entry.version, currentVersion) <= 0);
        const hasMajor = relevantChanges.some(c => c.updateType === ChapterUpdateType.MAJOR);
        if (hasMajor)
            return ChapterUpdateType.MAJOR;
        const hasMinor = relevantChanges.some(c => c.updateType === ChapterUpdateType.MINOR);
        if (hasMinor)
            return ChapterUpdateType.MINOR;
    }
    // Fallback: check version number change
    const comparison = compareVersions(currentVersion, completedVersion);
    if (comparison > 0) {
        // Major version change (1.x -> 2.x) = major update
        const currentMajor = parseInt(currentVersion.split('.')[0]);
        const completedMajor = parseInt(completedVersion.split('.')[0]);
        if (currentMajor > completedMajor) {
            return ChapterUpdateType.MAJOR;
        }
        // Minor version change (1.0 -> 1.1) = minor update
        return ChapterUpdateType.MINOR;
    }
    return ChapterUpdateType.NONE;
};
